/**
 * The words in a template's text slots — the lines a couple may rewrite
 * (registry.js TEXT_SLOTS).
 *
 * Pure: `t` is passed in (any i18next `t`, or a fixed one for another
 * language), so the template's model builder and the preview's `defaults`
 * message read slots exactly the same way.
 */
import { TEMPLATES, TEXT_SLOTS } from "./registry.js";

/** The template's own line for a slot, in the language `t` speaks. */
export function slotDefault(t, slot, eventType) {
  const def = TEXT_SLOTS[slot];
  if (!def) return "";
  return def.eventTypeContext ? t(def.i18nKey, { context: eventType }) : t(def.i18nKey);
}

/**
 * What each of a template's slots shows: the couple's line, or the template's
 * own when they left it blank (blank is what the API stores as "use ours").
 */
export function slotTexts(t, templateId, invitation, eventType) {
  const own = invitation?.texts ?? {};
  return Object.fromEntries(
    Object.keys(TEMPLATES[templateId].texts).map((slot) => {
      const line = own[slot];
      return [
        slot,
        typeof line === "string" && line.trim() ? line : slotDefault(t, slot, eventType),
      ];
    }),
  );
}

/** The template's own line for every one of its slots — what the editor offers to start from. */
export function slotDefaults(t, templateId, eventType) {
  return Object.fromEntries(
    Object.keys(TEMPLATES[templateId].texts).map((slot) => [
      slot,
      slotDefault(t, slot, eventType),
    ]),
  );
}

/**
 * `line` cut to at most `max` characters (none when max is 0 or less).
 * Lengths are UTF-16 units, as the API and the editor's fields count them; a
 * cut never leaves half of a character (an emoji) behind — saved, half an
 * emoji shows as "�" on the guests' invitation.
 */
export function cutLine(line, max) {
  if (line.length <= max) return line;
  let end = Math.max(0, max);
  const last = line.charCodeAt(end - 1);
  if (last >= 0xd800 && last <= 0xdbff) end -= 1;
  return line.slice(0, end);
}

/**
 * A line typed on the preview, fitted to its slot: `value` is what may be
 * posted to the editor — never longer than the slot's max, which is what the
 * API accepts — and `fix` what the line on the page has to become: "restore"
 * (back to `value`, the line from before a composition), "cut" (cut back to
 * `value`) or null (left as typed).
 *
 * A typed letter that doesn't fit is refused before it lands. Greek and
 * Android keyboards compose a word instead, and a composition can't be
 * refused keystroke by keystroke, so a word can overfill the line. Such a
 * word is refused whole, where it was typed: what is posted stays the line
 * from before the composition (`composedFrom`), and once the composition is
 * over the page goes back to it too — never while it is under way, as
 * rewriting the text under the keyboard breaks the word it is building.
 */
export function fitTypedLine(typed, max, { composedFrom = null, composing = false } = {}) {
  if (typed.length <= max) return { value: typed, fix: null };
  if (composedFrom !== null && composedFrom.length <= max) {
    return { value: composedFrom, fix: composing ? null : "restore" };
  }
  return { value: cutLine(typed, max), fix: composing ? null : "cut" };
}
